import type { ProjectHandle, ProjectSource, ProjectSourceId } from "@loumy/contracts";
import { create } from "zustand";

// D69/D72 — Zustand des geöffneten Projekts + Multi-Doc-Hosts (Client-only, in-memory). NICHT
// persistiert (D51/D47: das Projekt ist eine lokale Daten-Ebene; FS-Access-Handles werden bewusst
// nicht serialisiert — nach Reload muss der User neu öffnen). EINE offene Projekt-/Editor-Instanz
// (§11). Die async-Orchestrierung (lesen/parsen/schreiben) lebt im Hook useProjectHost; dieser Store
// hält nur den Roh-Zustand + simple Setter (testbar, frei von I/O).

interface ProjectStoreState {
  /** Aktive Projekt-Quelle (FS-Access | CLI) — null = kein Projekt offen. */
  source: ProjectSource | null;
  sourceId: ProjectSourceId | null;
  handle: ProjectHandle | null;

  /** Dokument-Namen im Set (ohne Verzeichnis/Endung), sortiert. */
  documents: string[];
  /** Aktuell geöffnetes Dokument (Name) oder null. */
  activeDocument: string | null;
  /** Kompakter JSON-Snapshot beim letzten Öffnen/Speichern — für Dirty-Vergleich. */
  savedSnapshot: string | null;

  busy: boolean;
  error: string | null;

  /** Steigt, wenn sich Projekt-DATEN auf der Platte ändern (z.B. nach D74-Import + `generate`) — Konsumenten
   *  (z.B. der Editor-Descriptor-Cache) laden dann neu, ohne dass die `source`-Identität wechselt. */
  dataEpoch: number;

  /** D102 (Codex-P1) — steigt bei jedem (Re-)ÖFFNEN eines Dokuments (openDocument/newDocument, auch der
   *  externe Sync-Re-Open D78), NICHT beim eigenen Autosave (saveDocument). Konsumenten mit eigenem
   *  Editier-Puffer (z.B. der Blockly-Workspace, der den `graph`-Prop bewusst nur per Dokument-Key neu
   *  lädt) hängen diesen Zähler an ihren Key — sonst bliebe nach einem externen Reload der alte Stand
   *  stehen und der nächste Edit überschriebe die externe Änderung. Monoton, wird nie zurückgesetzt. */
  openEpoch: number;

  setProject: (source: ProjectSource, handle: ProjectHandle) => void;
  closeProject: () => void;
  setDocuments: (names: string[]) => void;
  setActiveDocument: (name: string | null, snapshot: string | null) => void;
  setBusy: (busy: boolean) => void;
  setError: (error: string | null) => void;
  bumpDataEpoch: () => void;
  bumpOpenEpoch: () => void;
}

export const useProjectStore = create<ProjectStoreState>((set) => ({
  source: null,
  sourceId: null,
  handle: null,
  documents: [],
  activeDocument: null,
  savedSnapshot: null,
  busy: false,
  error: null,
  dataEpoch: 0,
  openEpoch: 0,

  setProject: (source, handle) => set({ source, sourceId: source.id, handle, error: null }),
  closeProject: () =>
    set({
      source: null,
      sourceId: null,
      handle: null,
      documents: [],
      activeDocument: null,
      savedSnapshot: null,
      error: null,
    }),
  setDocuments: (names) => set({ documents: names }),
  setActiveDocument: (name, snapshot) => set({ activeDocument: name, savedSnapshot: snapshot }),
  setBusy: (busy) => set({ busy }),
  setError: (error) => set({ error }),
  bumpDataEpoch: () => set((s) => ({ dataEpoch: s.dataEpoch + 1 })),
  bumpOpenEpoch: () => set((s) => ({ openEpoch: s.openEpoch + 1 })),
}));
